import { Search, X } from 'lucide-react'
import { MultiSelect } from '../shared/MultiSelect'
import { COUNTRIES } from '../../lib/constants'
import type { FunnelFilters as FiltersType } from '../../types'

interface FunnelFiltersProps {
  filters: FiltersType
  onFilterChange: <K extends keyof FiltersType>(key: K, value: FiltersType[K]) => void
  onReset: () => void
  hasActiveFilters: boolean
}

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'draft', label: 'Draft' },
  { value: 'inactive', label: 'Inactive' },
]

const FUNNEL_TYPE_OPTIONS = [
  { value: 'post_purchase', label: 'Post Purchase' },
  { value: 'pre_purchase', label: 'Pre Purchase' },
  { value: 'landing_page', label: 'Landing Page' },
]

const TRAFFIC_TYPE_OPTIONS = [
  { value: 'acq', label: 'ACQ' },
  { value: 'organic', label: 'Organic' },
  { value: 'paid', label: 'Paid' },
  { value: 'email', label: 'Email' },
  { value: 'social', label: 'Social' },
]

const CURRENCY_OPTIONS = [
  { value: 'USD', label: 'USD' },
  { value: 'CAD', label: 'CAD' },
  { value: 'AUD', label: 'AUD' },
  { value: 'GBP', label: 'GBP' },
  { value: 'EUR', label: 'EUR' },
  { value: 'NZD', label: 'NZD' },
]

const PRODUCT_TYPE_OPTIONS = [
  { value: 'physical', label: 'Physical' },
  { value: 'digital', label: 'Digital' },
  { value: 'subscription', label: 'Subscription' },
]

export function FunnelFilters({
  filters,
  onFilterChange,
  onReset,
  hasActiveFilters,
}: FunnelFiltersProps) {
  const countryOptions = COUNTRIES.map((c) => ({
    value: c.code,
    label: c.name,
    icon: c.flag,
  }))

  return (
    <div className="mb-6 space-y-3">
      {/* Row 1: Search (full width) */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fn-text-muted pointer-events-none" />
        <input
          type="text"
          className="w-full h-10 pl-9 pr-3 border border-fn-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
          placeholder="Search funnels..."
          value={filters.search}
          onChange={(e) => onFilterChange('search', e.target.value)}
        />
      </div>

      {/* Row 2: Global filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-44">
          <MultiSelect
            options={countryOptions}
            selected={filters.countries}
            onChange={(countries) => onFilterChange('countries', countries)}
            placeholder="All countries"
          />
        </div>

        <div className="w-32">
          <MultiSelect
            options={CURRENCY_OPTIONS}
            selected={filters.currency}
            onChange={(currency) => onFilterChange('currency', currency)}
            placeholder="Currency"
          />
        </div>

        <div className="w-40">
          <MultiSelect
            options={TRAFFIC_TYPE_OPTIONS}
            selected={filters.trafficType}
            onChange={(trafficType) => onFilterChange('trafficType', trafficType)}
            placeholder="Traffic Type"
          />
        </div>

        <div className="w-40">
          <MultiSelect
            options={PRODUCT_TYPE_OPTIONS}
            selected={filters.productType}
            onChange={(productType) => onFilterChange('productType', productType)}
            placeholder="Product Type"
          />
        </div>

        <div className="w-40">
          <MultiSelect
            options={STATUS_OPTIONS}
            selected={filters.status}
            onChange={(status) => onFilterChange('status', status)}
            placeholder="All Status"
          />
        </div>

        <div className="w-48">
          <MultiSelect
            options={FUNNEL_TYPE_OPTIONS}
            selected={filters.funnelType}
            onChange={(funnelType) => onFilterChange('funnelType', funnelType)}
            placeholder="All Types"
          />
        </div>

        <div className="flex items-center gap-2 h-10">
          <input
            type="date"
            value={filters.dateFrom}
            onChange={(e) => onFilterChange('dateFrom', e.target.value)}
            className="h-10 px-3 border border-fn-border rounded-md text-sm bg-fn-card text-fn-text"
            aria-label="Date from"
          />
          <span className="text-fn-text-muted text-sm">to</span>
          <input
            type="date"
            value={filters.dateTo}
            onChange={(e) => onFilterChange('dateTo', e.target.value)}
            className="h-10 px-3 border border-fn-border rounded-md text-sm bg-fn-card text-fn-text"
            aria-label="Date to"
          />
        </div>

        {hasActiveFilters && (
          <button
            type="button"
            className="flex items-center gap-1.5 px-3 h-10 rounded-md border border-fn-border text-sm text-fn-text-muted transition-colors hover:bg-fn-hover hover:text-fn-text-sub"
            onClick={onReset}
          >
            <X className="w-4 h-4" />
            Clear
          </button>
        )}
      </div>
    </div>
  )
}
